import React, { useState, useEffect, forwardRef } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  ImageOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { URLMediaCache } from '../../services/cache/URLMediaCache';
import { useCachedImage } from '../../hooks/useURLMediaCache';

export interface CachedImageProps {
  /** The source URL of the image */
  src: string;
  /** Alt text for accessibility */
  alt: string;
  /** Optional className for styling */
  className?: string;
  /** Optional inline styles */
  style?: React.CSSProperties;
  /** Width of the image */
  width?: number | string;
  /** Height of the image */
  height?: number | string;
  /** Whether to show cache status indicator (default: false) */
  showCacheStatus?: boolean;
  /** Loading placeholder type */
  loadingType?: 'spinner' | 'skeleton' | 'none';
  /** Whether to automatically cache the image (default: true) */
  autoCache?: boolean;
  /** Fallback image URL if main image fails */
  fallbackSrc?: string;
  /** Custom error message */
  errorMessage?: string;
  /** Whether to show error UI or just fallback (default: false) */
  showErrorUI?: boolean;
  /** Object fit style for the image */
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  /** Border radius */
  borderRadius?: number | string;
  /** onClick handler */
  onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
  /** onLoad handler */
  onLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  /** onError handler */
  onError?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

/**
 * CachedImage component with 30-day TTL caching
 * 
 * Provides intelligent image caching to reduce R2 costs while maintaining
 * fast loading times and graceful fallbacks.
 */
export const CachedImage = forwardRef<HTMLImageElement, CachedImageProps>(({
  src,
  alt,
  className,
  style,
  width,
  height,
  showCacheStatus = false,
  loadingType = 'skeleton',
  autoCache = true,
  fallbackSrc,
  errorMessage,
  showErrorUI = false,
  objectFit = 'contain',
  borderRadius = 0,
  onClick,
  onLoad,
  onError,
}, ref) => {
  const { user } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  // Use caching for the image
  const { 
    mediaUrl: cachedImageUrl, 
    isLoading: isCacheLoading, 
    error: cacheError, 
    isCached 
  } = useCachedImage(src, {
    autoCache,
    preferCache: true,
    cacheTimeout: 5000 // 5 second timeout for images
  });

  // Initialize URLMediaCache when user is available
  useEffect(() => {
    if (user?.email) {
      URLMediaCache.initialize(user.email);
    }
  }, [user?.email]);

  // Handle image load
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setIsImageLoaded(true);
    setImageError(false);
    onLoad?.(event);
  };

  // Handle image error
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    
    // Try fallback if available and not already using fallback
    if (fallbackSrc && !usingFallback) {
      setUsingFallback(true);
      setImageError(false);
      // The effect below will handle switching to fallback
      return;
    }
    
    onError?.(event);
  };

  // Get the final image URL to use
  const finalImageUrl = usingFallback ? fallbackSrc : (cachedImageUrl || src);
  
  // Determine if we should show loading
  const showLoading = (isCacheLoading && !imageError) || (!isImageLoaded && !imageError && loadingType !== 'none');

  // Container styles
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    width: width || 'auto',
    height: height || 'auto',
    borderRadius,
    overflow: 'hidden',
    ...style,
  };

  // Image styles
  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit,
    display: isImageLoaded ? 'block' : 'none',
  };

  // Show error UI if enabled and there's an error
  if (imageError && showErrorUI) {
    return (
      <Box 
        sx={{ 
          ...containerStyle,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          border: '1px dashed',
          borderColor: 'grey.300',
          bgcolor: 'grey.50',
        }}
        className={className}
      >
        <ErrorOutline sx={{ color: 'grey.400', fontSize: 40, mb: 1 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {errorMessage || 'Failed to load image'}
        </Typography>
        {cacheError && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Cache: {cacheError}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={containerStyle} className={className}>
      {/* Cache status indicator */}
      {showCacheStatus && (isCached || cacheError) && (
        <Chip 
          label={isCached ? 'Cached' : 'Cache unavailable'} 
          size="small" 
          color={isCached ? 'success' : 'warning'}
          sx={{ 
            position: 'absolute', 
            top: 4, 
            right: 4, 
            zIndex: 2,
            fontSize: '0.65rem',
            height: 20,
          }} 
        />
      )}

      {/* Loading indicator */}
      {showLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
        >
          {loadingType === 'spinner' ? (
            <CircularProgress size={24} />
          ) : loadingType === 'skeleton' ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height="100%"
              animation="wave"
              sx={{ bgcolor: 'grey.200' }}
            />
          ) : null}
        </Box>
      )}

      {/* Placeholder when no image available */}
      {!finalImageUrl && !showLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            bgcolor: 'grey.100',
            color: 'grey.400',
          }}
        >
          <ImageOutlined sx={{ fontSize: 40 }} />
        </Box>
      )}

      {/* Actual image */}
      {finalImageUrl && (
        <img
          ref={ref}
          src={finalImageUrl}
          alt={alt}
          style={imageStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
          onClick={onClick}
        />
      )}
    </Box>
  );
});

CachedImage.displayName = 'CachedImage';

/**
 * Specialized cached image for avatars
 */
export const CachedAvatar: React.FC<Omit<CachedImageProps, 'objectFit' | 'borderRadius'> & {
  size?: number;
}> = ({ size = 40, ...props }) => {
  return (
    <CachedImage
      {...props}
      width={size}
      height={size}
      objectFit="cover"
      borderRadius="50%"
      loadingType="skeleton"
    />
  );
};

/**
 * Specialized cached image for thumbnails
 */
export const CachedThumbnail: React.FC<Omit<CachedImageProps, 'objectFit'> & {
  aspectRatio?: number;
}> = ({ aspectRatio = 16/9, width = 200, ...props }) => {
  const height = typeof width === 'number' ? width / aspectRatio : undefined;
  
  return (
    <CachedImage
      {...props}
      width={width}
      height={height}
      objectFit="cover"
      borderRadius={8}
      loadingType="skeleton"
      showCacheStatus={false}
    />
  );
};

/**
 * Specialized cached image for evidence/screenshots
 * Uses URL-based caching for R2 cost reduction with identical styling
 */
export const CachedEvidence: React.FC<Omit<CachedImageProps, 'showCacheStatus' | 'showErrorUI'> & {
  /** Force fallback to regular img tag (for debugging) */
  forceFallback?: boolean;
}> = ({
  errorMessage = 'Evidence image unavailable',
  forceFallback = false,
  autoCache = true,
  ...props
}) => {
  const { user } = useAuth();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Initialize cache when user is available
  useEffect(() => {
    if (user?.email) {
      URLMediaCache.initialize(user.email);
    }
  }, [user?.email]);

  // Use URL-based caching hook (but we'll always use original URL for styling consistency)
  const { isCached } = useCachedImage(props.src, {
    autoCache: autoCache && !forceFallback,
    cacheTimeout: 5000
  });

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true);
    setImageError(false);
    props.onLoad?.(event);
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setImageError(true);
    props.onError?.(event);
  };

  // Always use regular img tag with identical styling - caching happens transparently
  return (
    <img
      src={props.src}
      alt={props.alt}
      style={{
        width: props.width,
        height: props.height,
        objectFit: props.objectFit || 'contain',
        borderRadius: props.borderRadius ?? 4,
        // Only add border if not explicitly overridden
        ...(props.style?.border === 'none' ? {} : {
          border: '1px solid #e0e0e0',
        }),
        ...props.style
      }}
      onClick={props.onClick}
      onLoad={handleImageLoad}
      onError={handleImageError}
    />
  );
};

export default CachedImage;